export interface CarouselSettings {
  count: number;
  refreshPct: number;
  refreshMinutes: number;
}

export interface HomeLayout {
  order: string[];
  hidden: string[];
  carouselSettings?: Record<string, CarouselSettings>;
}

export interface HomeSectionDefinition {
  kind: string;
  label: string;
  hint: string;
}

export const DEFAULT_CAROUSEL: CarouselSettings = {
  count: 12,
  refreshPct: 10,
  refreshMinutes: 240,
};

/** Every fixed Home row plus the recommendation kinds the server can emit.
 * Preferences augments this with the live recommendation response so newly
 * added and per-profile genre rows are editable too. */
export const HOME_SECTIONS: HomeSectionDefinition[] = [
  { kind: "netflix_trending", label: "Trending on Netflix", hint: "Netflix catalog picks streamed through Real-Debrid" },
  { kind: "curiosity_featured", label: "Featured on CuriosityStream", hint: "Featured documentaries from CuriosityStream" },
  { kind: "provider_apple", label: "Hot on Apple TV+", hint: "Trending Apple TV+ titles" },
  { kind: "provider_hbo", label: "Hot on HBO Max", hint: "Trending HBO Max titles" },
  { kind: "provider_cable", label: "Hot on Cable", hint: "Trending cable titles" },
  { kind: "provider_nfb", label: "Hot on NFB", hint: "National Film Board of Canada picks" },
  { kind: "apps", label: "Apps", hint: "Launcher tiles for YouTube, Netflix, Premium Club, Crunchyroll" },
  { kind: "continue", label: "Continue Watching", hint: "Things you've started but not finished" },
  { kind: "pinned", label: "Pinned", hint: "Titles pinned to your Home page" },
  { kind: "highly_recommended", label: "Highly Recommended", hint: "Items other profiles sent you" },
  { kind: "movies", label: "Movies", hint: "Movie recommendations" },
  { kind: "tv_series", label: "TV Series", hint: "Series recommendations" },
  { kind: "youtube", label: "YouTube", hint: "YouTube recommendations" },
  { kind: "because_loved", label: "Because you loved …", hint: "Anchored on a 10/10 onboarding rating" },
  { kind: "from_creators", label: "Latest from creators you follow", hint: "Unwatched recent uploads from your YouTube subs" },
  { kind: "acclaimed_in_genre", label: "Critically acclaimed in [genre]", hint: "TMDB-rated 7.0+ in your top genre, last 25 years" },
  { kind: "hidden_gems", label: "Hidden gems for you", hint: "High TMDB rating, low vote count" },
  { kind: "vault", label: "From the vault — classics", hint: "Classic films 1940–1985 in your top genre" },
  { kind: "new_arrivals", label: "New arrivals", hint: "Recently added titles" },
  { kind: "quick_picks", label: "Quick picks", hint: "Shorter titles for when time is limited" },
  { kind: "for_you", label: "For You", hint: "Diversity-capped ranker pick" },
  { kind: "live_now", label: "Live Now", hint: "Live TV channels" },
  { kind: "anime_trending", label: "Trending Anime", hint: "AniList trending, launches Crunchyroll on click" },
  { kind: "anime_top", label: "Top Rated Anime", hint: "AniList all-time favorites" },
  { kind: "anime_season", label: "This Season", hint: "Anime from the current season" },
];

export const HOME_SERVICE_KINDS = HOME_SECTIONS.slice(0, 6).map((s) => s.kind);

export function applyHomeLayout<T extends { kind: string }>(
  rows: T[],
  layout: Pick<HomeLayout, "order" | "hidden"> | undefined,
): T[] {
  if (!layout) return rows;
  const hidden = new Set(layout.hidden);
  const visible = rows.filter((row) => !hidden.has(row.kind));
  if (layout.order.length === 0) return visible;
  const byKind = new Map(visible.map((row) => [row.kind, row]));
  const used = new Set<string>();
  const out: T[] = [];
  for (const kind of layout.order) {
    const row = byKind.get(kind);
    if (row) {
      out.push(row);
      used.add(kind);
    }
  }
  // New server rows not present in an older saved layout remain visible.
  for (const row of visible) if (!used.has(row.kind)) out.push(row);
  return out;
}
